import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { isEqual } from 'lodash-es';
import { Alert, Box, Card, Heading, Icon, Paragraph, VisuallyHidden } from '@zillow/constellation-10';
import { IconFileFilled } from '@zillow/constellation-icons';
import { formatRequirement, REQUIRED_DOCUMENT_COPY } from '@zg-rentals/applications-components';
import type { Answers, Document } from '../../../../models/rental-application';
import type { FeatureFlags } from '../../../../contexts/ApplicationContext';
import { useApplicationContext, useFeatureFlags } from '../../../../contexts/ApplicationContext';
import { getIsGuarantorWizard, getIsIncomeDocumentsStepVisible } from '../../../../constants/wizard';
import type { UploadTarget } from '../../../../hooks/useIncomeDocumentUploads';
import { getRowUploadIds, useIncomeDocumentUploads } from '../../../../hooks/useIncomeDocumentUploads';
import { PendingRemovalsContext, usePendingDocumentRemovals } from '../../../../hooks/usePendingDocumentRemovals';
import { useSavedDocuments } from '../../../../hooks/useSavedDocuments';
import { StepDocumentDetailsContext, useStepDocumentDetails } from '../../../../hooks/useStepDocumentDetails';
import type { IncomeSourceGroup, PickedRow, RequestedRow } from '../../../../utils/incomeDocuments';
import {
  getIncomeDocumentsModel,
  getModelDocuments,
  getOtherDocuments,
  getPickableDocuments,
  getPickerCandidates,
  hasNoIncomeDocuments,
  toTypePhrase,
  withPendingPicks,
} from '../../../../utils/incomeDocuments';
import { PendingPicksContext, usePendingDocumentPicks } from '../../../../hooks/usePendingDocumentPicks';
import { getIncomeDocEncouragementText } from '../../../../constants/incomeDocs';
import { AddFilesButton } from './IncomeDocuments/AddFilesButton';
import { DocumentContainer } from './IncomeDocuments/DocumentContainer';
import type { FailedUploads } from './IncomeDocuments/FailedUploadCard';
import { DocumentGroup } from './IncomeDocuments/DocumentGroup';
import type { IdentifySavedDocumentsProps } from '../Documents/IdentifySavedDocuments';
import { IdentifySavedDocuments } from '../Documents/IdentifySavedDocuments';
import { RequirementsProgress } from './IncomeDocuments/RequirementsProgress';

type RowUpload = { pending: Array<Document>; failed: FailedUploads; action?: ReactNode };

type GetUpload = (target: UploadTarget, label: string) => RowUpload;

type PickerProps = Omit<IdentifySavedDocumentsProps, 'focusHeading'>;

type GetPicker = (row: RequestedRow) => PickerProps | undefined;

function RequestedRowContainer({
  row,
  tag,
  isRequested,
  documents,
  upload,
  picker,
}: {
  row: RequestedRow;
  tag?: string;
  isRequested: boolean;
  documents: Array<Document>;
  upload?: RowUpload;
  picker?: PickerProps;
}) {
  const requirement = formatRequirement(
    row.section.parameterQuantity ?? undefined,
    row.section.parameterUnit ?? undefined,
  );
  const removeLabel = !isRequested ? 'Remove' : row.section.incomeId ? 'Remove from this section' : undefined;

  return (
    <DocumentContainer
      heading={REQUIRED_DOCUMENT_COPY[row.documentType].label}
      tag={tag}
      requirement={requirement || undefined}
      isRequested={isRequested}
      documents={[...documents, ...(upload?.pending ?? [])]}
      removal={removeLabel ? { label: removeLabel, section: row.section } : undefined}
      canDelete={isRequested}
      action={upload?.action}
      failed={upload?.failed}
      picker={picker && (({ focusHeading }) => <IdentifySavedDocuments {...picker} focusHeading={focusHeading} />)}
    />
  );
}

function IncomeSourceDocuments({
  group,
  isTemplate,
  isGuarantor,
  rowUploadIds,
  getUpload,
  hasSavedFilesAlert,
  getPicker,
}: {
  group: IncomeSourceGroup;
  isTemplate: boolean;
  isGuarantor: boolean;
  rowUploadIds: ReadonlySet<string>;
  getUpload: GetUpload;
  hasSavedFilesAlert: boolean;
  getPicker: GetPicker;
}) {
  const incomeId = group.income.id;
  const supporting = incomeId ? getUpload({ kind: 'supporting', incomeId }, `Add files to ${group.label}`) : undefined;

  return (
    <DocumentGroup
      title={group.label}
      description={
        !isTemplate && group.income.type ? getIncomeDocEncouragementText(group.income.type, isGuarantor) : undefined
      }
    >
      {hasSavedFilesAlert && (
        <Alert tone="info">
          You may already have some of these documents saved. Add them below so you don’t upload them again.
        </Alert>
      )}
      {group.requestedRows.map((row) => (
        <RequestedRowContainer
          key={row.documentType}
          row={row}
          isRequested
          documents={row.documents}
          upload={
            incomeId
              ? getUpload(
                  { kind: 'incomeRow', incomeId, documentType: row.documentType },
                  `Add files to ${REQUIRED_DOCUMENT_COPY[row.documentType].label} for ${group.label}`,
                )
              : undefined
          }
          picker={getPicker(row)}
        />
      ))}
      <DocumentContainer
        heading={isTemplate ? 'Supporting documents' : 'Income documents'}
        tag={isTemplate ? 'Optional' : undefined}
        documents={[
          ...group.supportingDocuments.filter(({ id }) => !id || !rowUploadIds.has(id)),
          ...(supporting?.pending ?? []),
        ]}
        canDelete
        action={supporting?.action}
        failed={supporting?.failed}
      />
    </DocumentGroup>
  );
}

function RowsGroup({
  title,
  description,
  rows,
  isRequested,
  getTag,
  getRowUpload,
  getPicker,
}: {
  title: string;
  description?: string;
  rows: Array<RequestedRow>;
  isRequested: boolean;
  getTag?: (row: RequestedRow) => string | undefined;
  getRowUpload?: (row: RequestedRow) => RowUpload;
  getPicker?: GetPicker;
}) {
  return (
    <DocumentGroup title={title} description={description}>
      {rows.map((row) => (
        <RequestedRowContainer
          key={`${row.documentType}-${row.section.incomeId ?? 'application'}`}
          row={row}
          tag={getTag?.(row)}
          isRequested={isRequested}
          documents={row.documents}
          upload={getRowUpload?.(row)}
          picker={getPicker?.(row)}
        />
      ))}
    </DocumentGroup>
  );
}

export function RequiredDocumentsStep() {
  const { application, featureFlags, listing } = useApplicationContext();
  const isTemplateEligible = featureFlags.hasRequiredDocuments === true;
  const removals = usePendingDocumentRemovals(application.id as string, application.answers);
  const picks = usePendingDocumentPicks(application.id as string, application.answers);
  const model = useMemo(
    () => getIncomeDocumentsModel(withPendingPicks(removals.answers, picks.entries), { isTemplateEligible }),
    [removals.answers, picks.entries, isTemplateEligible],
  );
  const isTemplate = model.mode === 'template';
  const uploads = useIncomeDocumentUploads(application.id as string, application.answers);
  const requesterName = listing.listingOwner?.name?.trim() || 'The rental manager';
  const rowUploadIds = getRowUploadIds(uploads.pending, uploads.failed);
  const getUpload: GetUpload = (target, label) => ({
    pending: uploads.pending
      .filter((entry) => isEqual(entry.target, target))
      .map(({ fileName }): Document => ({ fileName })),
    failed: {
      uploads: uploads.failed.filter((entry) => isEqual(entry.target, target)),
      onRetry: uploads.retry,
      onRemove: uploads.dismiss,
    },
    action: uploads.hasRoom(target) ? (
      <AddFilesButton label={label} onFiles={(files) => uploads.addFiles(target, files)} />
    ) : undefined,
  });
  const saved = useSavedDocuments();
  const shownDocuments = getModelDocuments(model);
  const otherDocuments = getOtherDocuments(saved.documents, shownDocuments, removals.deletedIds);
  // The saved-files list already has the details, so only files that wait for redaction are polled
  const details = useStepDocumentDetails([
    ...shownDocuments,
    ...otherDocuments.filter(({ isRedactionCheckComplete }) => !isRedactionCheckComplete),
  ]);
  const { latestFailure } = uploads;
  const toPickable = (documents: Array<Document>) =>
    getPickableDocuments(
      documents.map((document) => (document.id && details.detailOf(document.id)) || document),
      {
        now: Date.now(),
        isExcluded: (documentId) => removals.actions.isDetaching(documentId) || rowUploadIds.has(documentId),
      },
    );

  const getPicker = (row: RequestedRow): PickerProps | undefined => {
    const pickedRow: PickedRow = { documentType: row.documentType, incomeId: row.section.incomeId ?? undefined };
    const candidates = toPickable(getPickerCandidates(model, pickedRow.incomeId, otherDocuments));
    if (candidates.length === 0) {
      return undefined;
    }
    return {
      typeLabel: toTypePhrase(REQUIRED_DOCUMENT_COPY[row.documentType].label),
      candidates,
      onAdd: (documentIds) => {
        documentIds.forEach((documentId) => removals.actions.cancelDetach({ documentId, ...pickedRow }));
        picks.pick(pickedRow, documentIds);
      },
    };
  };

  const getHasSavedFilesAlert = ({ income, requestedRows }: IncomeSourceGroup) =>
    !!income.id &&
    (requestedRows.length > 0 || model.applicationRows.length > 0) &&
    toPickable(getPickerCandidates(model, income.id)).length > 0;

  const sourceLabels = new Map(
    model.incomeSources.flatMap(({ income, label }) => (income.id ? [[income.id, label]] : [])),
  );

  return (
    <PendingRemovalsContext.Provider value={removals.actions}>
      <PendingPicksContext.Provider value={picks}>
        <StepDocumentDetailsContext.Provider value={details}>
          {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Constellation has no output component */}
          <VisuallyHidden role="status">
            {/* A key per failure makes the same sentence twice new content */}
            <span key={latestFailure?.count}>
              {latestFailure && `${latestFailure.fileName} wasn’t added. Your other files are saved.`}
            </span>
          </VisuallyHidden>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Income documents
          </Heading>
          <Paragraph css={{ marginBottom: 'default', overflowWrap: 'anywhere' }}>
            {isTemplate
              ? `${requesterName} asked for these documents to support your application. You can submit without them, but they help your application get reviewed faster.`
              : 'Add documents that support each income source. They help your application get reviewed faster.'}
          </Paragraph>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
            {details.isError && <Alert tone="critical">We couldn’t load your files</Alert>}
            {(details.areDocumentsExpired || saved.areDocumentsExpired) && (
              <Alert tone="warning">
                Some income documents you added before have expired. Add the latest documents for each income source.
              </Alert>
            )}
            {isTemplate && <RequirementsProgress covered={model.coveredCount} total={model.requestedCount} />}
            {model.incomeSources.length === 0 && (
              <Alert tone="info">Add an income source on the Current income step to add documents for it.</Alert>
            )}
            {model.incomeSources.map((group) => (
              <IncomeSourceDocuments
                key={group.income.id ?? group.index}
                group={group}
                isTemplate={isTemplate}
                isGuarantor={getIsGuarantorWizard(featureFlags, application.role)}
                rowUploadIds={rowUploadIds}
                getUpload={getUpload}
                hasSavedFilesAlert={getHasSavedFilesAlert(group)}
                getPicker={getPicker}
              />
            ))}
            {model.applicationRows.length > 0 && (
              <RowsGroup
                title="Additional financial documents"
                rows={model.applicationRows}
                isRequested
                getPicker={getPicker}
                getRowUpload={(row) =>
                  getUpload(
                    { kind: 'applicationRow', documentType: row.documentType },
                    `Add files to ${REQUIRED_DOCUMENT_COPY[row.documentType].label}`,
                  )
                }
              />
            )}
            {isTemplate && model.noLongerRequested.length > 0 && (
              <RowsGroup
                title="No longer requested"
                description="These files were added for a request that no longer applies to your income sources. They are still saved to your account."
                rows={model.noLongerRequested}
                isRequested={false}
                getTag={(row) => sourceLabels.get(row.section.incomeId ?? '')}
              />
            )}
            {otherDocuments.length > 0 && (
              <DocumentGroup
                title="Other documents"
                description="These documents are saved to your account and aren’t listed above. Delete any you no longer need."
              >
                <DocumentContainer heading="Saved documents" documents={otherDocuments} canDelete />
              </DocumentGroup>
            )}
          </Box>
          <Paragraph textStyle="body-sm" color="text.subtle" css={{ marginTop: 'default' }}>
            Sensitive information, like your SSN, may be automatically redacted when you upload a document. Preview your
            documents to verify that they don’t include sensitive information. Password-protected and encrypted
            documents aren’t supported. Each file must be smaller than 100MB.
          </Paragraph>
        </StepDocumentDetailsContext.Provider>
      </PendingPicksContext.Provider>
    </PendingRemovalsContext.Provider>
  );
}

export function RequiredDocumentsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconFileFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            Your documents
          </Heading>
          <Paragraph>
            Documents that support your income help rental managers verify it. The documents you add are saved to your
            account.
          </Paragraph>
        </>
      }
    />
  );
}

function RequiredDocumentsRecommendationBody() {
  return useFeatureFlags().hasRequiredDocuments === true ? (
    <Paragraph>
      The rental manager asked for documents that your application is still missing. You can submit without them, but
      adding them helps the rental manager decide faster.
    </Paragraph>
  ) : (
    <Paragraph>
      More than 60% of applicants include income documents, which can help the rental manager make a faster decision
      about your application. Consider adding current income documents, such as pay stubs or W-2s, to your application.
    </Paragraph>
  );
}

export const RequiredDocumentsRecommendation = {
  condition: ({ answers, featureFlags }: { answers: Answers; featureFlags?: FeatureFlags }) => {
    if (!getIsIncomeDocumentsStepVisible(featureFlags ?? {})) {
      return false;
    }
    if (featureFlags?.hasRequiredDocuments !== true) {
      return hasNoIncomeDocuments(answers);
    }
    const { coveredCount, requestedCount } = getIncomeDocumentsModel(answers, { isTemplateEligible: true });
    return coveredCount < requestedCount;
  },
  title: 'Income documents',
  body: <RequiredDocumentsRecommendationBody />,
};
